import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import {
  CreateTradeUnionPayload,
  DeleteTradeUnionsPayload,
  GetPaymentInfosByUnionIdPayload,
  GetUnionByIdPayload,
  runCreateTradeUnion,
  runDeleteTradeUnions,
  runGetPaymentInfosByUnionId,
  runGetUnionById,
  runGetUnions,
  runUpdateTradeUnion,
  Union,
  UnionListItem,
  UnionPayment,
  UnionPaymentInfo,
  UnionTermsOfPayment,
  UpdateTradeUnionPayload,
} from "./unionsApi";

// Adapter for managing UnionListItem
const unionListItemsAdapter = createEntityAdapter<
  UnionListItem,
  UnionListItem["id"]
>({
  selectId: (unionListItem) => unionListItem.id,
});

// Adapter for managing Union
const unionAdapter = createEntityAdapter<Union>();

// State for UnionListItem list
type UnionListState = EntityState<UnionListItem, UnionListItem["id"]> & {
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
  lastFetched?: number;
};

// State for individual Union
type UnionState = EntityState<Union, Union["id"]> & {
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
  lastFetched?: number;
};

export type SliceState = {
  unionList: UnionListState;
  unionDetails: UnionState;
};
export const getUnions = createAsyncThunk<any, any, { state: RootState }>(
  "unions/getUnions",
  async (payload: any) => {
    const response = await runGetUnions(payload);
    return response.data;
  },
);

export const getUnionById = createAsyncThunk<
  any,
  GetUnionByIdPayload,
  { state: RootState }
>("unions/getUnionById", async (payload: GetUnionByIdPayload) => {
  const response = await runGetUnionById(payload);
  return response.data as any;
});

export const createTradeUnion = createAsyncThunk<
  any,
  CreateTradeUnionPayload,
  { state: RootState }
>("unions/createTradeUnion", async (payload: CreateTradeUnionPayload) => {
  const response = await runCreateTradeUnion(payload);
  return response.data as any;
});

export const updateTradeUnion = createAsyncThunk<
  any,
  UpdateTradeUnionPayload,
  { state: RootState }
>("unions/updateTradeUnion", async (payload: UpdateTradeUnionPayload) => {
  const response = await runUpdateTradeUnion(payload);
  return response.data as any;
});

export const deleteTradeUnions = createAsyncThunk<
  any,
  DeleteTradeUnionsPayload,
  { state: RootState }
>("unions/deleteTradeUnions", async (payload: DeleteTradeUnionsPayload) => {
  const response = await runDeleteTradeUnions(payload);
  return response.data as any;
});

export const getPaymentInfosByUnionId = createAsyncThunk<
  UnionPaymentInfo[],
  GetPaymentInfosByUnionIdPayload,
  { state: RootState }
>(
  "unions/getPaymentInfosByUnionId",
  async (payload: GetPaymentInfosByUnionIdPayload) => {
    const response = await runGetPaymentInfosByUnionId(payload);
    return response.data;
  },
);

const updateLocalUnions = (
  event: any,
  state: any,
  existingLocalUnionChanges: UnionListItem[] | undefined,
) => {
  // Update localUnion object inside localUnions array in main union
  const existingLocalUnions: UnionListItem[] =
    state.unionDetails.entities[event.id]?.localUnions ?? [];
  const updatedLocalUnions: UnionListItem[] = existingLocalUnions.map(
    (localUnion: UnionListItem) => {
      if (localUnion.id === event.payload.localTradeUnionId) {
        const existingChanges: UnionListItem | undefined =
          existingLocalUnionChanges?.find(
            (change) => change.id === localUnion.id,
          ) || undefined;
        const baseUnion: UnionListItem = existingChanges
          ? existingChanges
          : localUnion;
        return {
          ...baseUnion,
          ...(event.name === "trdUnionNameChg"
            ? { name: event.payload.name }
            : {}),
          ...(event.name === "trdUnionAcrChg"
            ? { acronym: event.payload.acronym }
            : {}),
          ...(event.name === "trdUnionNumberChg"
            ? { tradeUnionNumber: event.payload.tradeUnionNumber }
            : {}),
        };
      }
      return localUnion;
    },
  );
  return updatedLocalUnions;
};

// Format terms of payment so that it replaces
// dots with commas for percentage and fixedAmount
const formatTermsOfPayment = (termsOfPayment: UnionTermsOfPayment) => {
  const formattedTermsOfPayment = {
    ...termsOfPayment,
    payments: termsOfPayment.payments.map((payment: UnionPayment) => {
      return {
        ...payment,
        percentage: payment.percentage?.replace(".", ","),
        fixedAmount: payment.fixedAmount?.replace(".", ","),
      };
    }),
  };
  return formattedTermsOfPayment;
};

export const unionsSlice = createSlice({
  name: "unions",
  initialState: {
    unionList: unionListItemsAdapter.getInitialState({
      status: "idle",
    }),
    unionDetails: unionAdapter.getInitialState({
      status: "idle",
    }),
  } as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    // UnionListItems reducers
    builder.addCase(getUnions.fulfilled, (state, action) => {
      unionListItemsAdapter.setAll(state.unionList, action.payload);
      state.unionList.status = "succeeded";
      state.unionList.lastFetched = Date.now(); // Set the timestamp
    });
    builder.addCase(getUnions.pending, (state) => {
      state.unionList.status = "pending";
    });
    builder.addCase(getUnions.rejected, (state, action) => {
      state.unionList.status = "failed";
      state.unionList.error = action.error.message;
    });

    // UnionDetails reducers

    // GetUnionById reducers
    builder.addCase(getUnionById.fulfilled, (state, action) => {
      unionAdapter.upsertOne(state.unionDetails, {
        ...action.payload,
        termsOfPayment: formatTermsOfPayment(action.payload.termsOfPayment),
        lastFetched: Date.now(),
      });
      state.unionDetails.status = "succeeded";
    });
    builder.addCase(getUnionById.pending, (state) => {
      state.unionDetails.status = "pending";
    });
    builder.addCase(getUnionById.rejected, (state, action) => {
      state.unionDetails.status = "failed";
      state.unionDetails.error = action.error.message;
    });

    // CreateTradeUnion reducers
    builder.addCase(createTradeUnion.fulfilled, (state, action) => {
      action.payload.forEach((event: any) => {
        if (event.name === "trdUnionNew") {
          // Check if it's a local union
          const localTradeUnionId = event?.payload?.localTradeUnionId;
          if (localTradeUnionId) {
            // Get list of already existing local unions of main union
            const existingLocalUnions: UnionListItem[] =
              state.unionDetails.entities[event.id]?.localUnions ?? [];
            // Update list of local unions with new local union
            const updatedLocalUnions: UnionListItem[] = [
              ...existingLocalUnions,
              {
                id: localTradeUnionId,
                name: event.payload.name,
                acronym: event.payload.acronym,
                tradeUnionNumber: event.payload.tradeUnionNumber,
              },
            ];
            // Update main union in unionDetails store with new local unions
            unionAdapter.updateOne(state.unionDetails, {
              id: event.id, // Event.id is always id of parent union
              changes: {
                localUnions: updatedLocalUnions,
              },
            });
            // Add union to unionDetails store, so it's ready to be viewed by selectedUnionId in UnionView
            unionAdapter.addOne(state.unionDetails, {
              ...event.payload,
              id: localTradeUnionId,
              parentId: event.id,
              lastFetched: Date.now(),
            });
          } else {
            // Add union to unionList store
            unionListItemsAdapter.addOne(state.unionList, {
              id: event.id,
              name: event.payload.name,
              acronym: event.payload.acronym,
              tradeUnionNumber: event.payload.tradeUnionNumber,
            });

            // Add union to unionDetails store, so it's ready to be viewed by selectedUnionId in UnionView
            unionAdapter.addOne(state.unionDetails, {
              ...event.payload,
              id: event.id,
              lastFetched: Date.now(),
            });
          }
        }
      });

      state.unionDetails.status = "succeeded";
    });
    builder.addCase(createTradeUnion.pending, (state) => {
      state.unionDetails.status = "pending";
    });
    builder.addCase(createTradeUnion.rejected, (state, action) => {
      state.unionDetails.status = "failed";
      state.unionDetails.error = action.error.message;
    });

    // DeleteTradeUnions reducers
    builder.addCase(deleteTradeUnions.fulfilled, (state, action) => {
      action.payload.forEach((event: any) => {
        if (event.name === "trdUnionDel") {
          // Check if it's a local union
          const localTradeUnionId = event?.payload?.localTradeUnionId;

          // Get list of already existing local unions of main union
          const existingLocalUnions: UnionListItem[] =
            state.unionDetails.entities[event.id]?.localUnions ?? [];

          if (localTradeUnionId) {
            // Update list of local unions by removing local union
            const updatedLocalUnions: UnionListItem[] =
              existingLocalUnions.filter(
                (localUnion: UnionListItem) =>
                  localUnion.id !== localTradeUnionId,
              );
            // Update main union in unionDetails store with filtered local unions
            unionAdapter.updateOne(state.unionDetails, {
              id: event.id, // Event.id is always id of parent union
              changes: {
                localUnions: updatedLocalUnions,
              },
            });
            // Remove union from unionDetails store
            unionAdapter.removeOne(state.unionDetails, localTradeUnionId);
          } else {
            // Remove union from unionList store
            unionListItemsAdapter.removeOne(state.unionList, event.id);

            // Remove union from unionDetails store
            unionAdapter.removeOne(state.unionDetails, event.id);

            // Remove possible main union's localUnions from unionDetails store,
            // as they are deleted as well with main union
            unionAdapter.removeMany(
              state.unionDetails,
              existingLocalUnions.map(
                (localUnion: UnionListItem) => localUnion.id,
              ),
            );
          }
        }
      });
      state.unionDetails.status = "succeeded";
    });
    builder.addCase(deleteTradeUnions.pending, (state) => {
      state.unionDetails.status = "pending";
    });
    builder.addCase(deleteTradeUnions.rejected, (state, action) => {
      state.unionDetails.status = "failed";
      state.unionDetails.error = action.error.message;
    });

    // UpdateTradeUnion reducers
    builder.addCase(updateTradeUnion.fulfilled, (state, action) => {
      // Create a map to group changes by union id
      const changesByUnionId: Record<string, Partial<Union>> = {};

      action.payload.forEach((event: any) => {
        // Use localTradeUnionId if available, otherwise use event id (eventId is always id of parent union)
        const unionId = event?.payload?.localTradeUnionId ?? event.id;
        if (!changesByUnionId[unionId]) {
          changesByUnionId[unionId] = {};
        }
        switch (event.name) {
          case "trdUnionBnkAccChg":
            changesByUnionId[unionId].bankAccount = event.payload.bankAccount;
            break;
          case "trdUnionAddressChg":
            changesByUnionId[unionId].address = event.payload.address;
            break;
          case "trdUnionBusinessIdChg":
            changesByUnionId[unionId].businessId = event.payload.businessId;
            break;
          case "trdUnionPhoneChg":
            changesByUnionId[unionId].phoneNumber = event.payload.phoneNumber;
            break;
          case "trdUnionPaymentInfoChg":
            changesByUnionId[unionId].unionPaymentInfo = [
              ...action.meta.arg.unionPaymentInfo,
            ];
            break;
          case "trdUnionAcrChg":
            changesByUnionId[unionId].acronym = event.payload.acronym;
            // If it's a local union, also update the main union's localUnions array
            if (event?.payload?.localTradeUnionId) {
              if (!changesByUnionId[event.id]) {
                changesByUnionId[event.id] = { localUnions: [] };
              }
              changesByUnionId[event.id].localUnions = updateLocalUnions(
                event,
                state,
                changesByUnionId[event.id]?.localUnions,
              );
            }
            break;
          case "trdUnionNameChg":
            changesByUnionId[unionId].name = event.payload.name;
            // If it's a local union, also update the main union's localUnions array
            if (event?.payload?.localTradeUnionId) {
              if (!changesByUnionId[event.id]) {
                changesByUnionId[event.id] = { localUnions: [] };
              }
              changesByUnionId[event.id].localUnions = updateLocalUnions(
                event,
                state,
                changesByUnionId[event.id]?.localUnions,
              );
            }
            break;
          case "trdUnionNumberChg":
            changesByUnionId[unionId].tradeUnionNumber =
              event.payload.tradeUnionNumber;
            // If it's a local union, also update the main union's localUnions array
            if (event?.payload?.localTradeUnionId) {
              if (!changesByUnionId[event.id]) {
                changesByUnionId[event.id] = { localUnions: [] };
              }
              changesByUnionId[event.id].localUnions = updateLocalUnions(
                event,
                state,
                changesByUnionId[event.id]?.localUnions,
              );
            }
            break;
          case "trdUnionTermsPaymentChg":
            changesByUnionId[unionId].termsOfPayment = formatTermsOfPayment(
              event.payload.termsOfPayment,
            );
            break;
          case "trdUnionLocalUnionsChg":
            changesByUnionId[unionId].localUnions = event.payload.localUnions;
            break;
          default:
        }
      });

      // Apply changes for each union (entity in unionDetails store)
      Object.entries(changesByUnionId).forEach(([entityId, changes]) => {
        if (Object.keys(changes).length > 0) {
          // Update unionDetails store
          unionAdapter.updateOne(state.unionDetails, {
            id: entityId,
            changes,
          });
        }
      });

      // Create a list of changes for unionList store
      const unionListChanges = Object.entries(changesByUnionId).map(
        ([entityId, changes]) => {
          const filteredChanges: Partial<UnionListItem> = {
            ...(changes.name !== undefined && { name: changes.name }),
            ...(changes.acronym !== undefined && { acronym: changes.acronym }),
            ...(changes.tradeUnionNumber !== undefined && {
              tradeUnionNumber: changes.tradeUnionNumber,
            }),
          };
          return {
            id: entityId,
            changes: filteredChanges,
          };
        },
      );

      // Also update unionList store
      unionListItemsAdapter.updateMany(state.unionList, unionListChanges);

      state.unionDetails.status = "succeeded";
    });
    builder.addCase(updateTradeUnion.pending, (state) => {
      state.unionDetails.status = "pending";
    });
    builder.addCase(updateTradeUnion.rejected, (state, action) => {
      state.unionDetails.status = "failed";
      state.unionDetails.error = action.error.message;
    });
  },
});

// Selectors
export const {
  selectAll: selectAllUnionListItems,
  selectById: selectUnionListItemById,
} = unionListItemsAdapter.getSelectors<RootState>(
  (state) => state.unions.unionList,
);

export const { selectById: selectUnionDetailsById } =
  unionAdapter.getSelectors<RootState>((state) => state.unions.unionDetails);

export const unionsReducer = unionsSlice.reducer;
